import { PracticeQueryParams } from 'constants/application-constants'

import { useCallback, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from 'hooks'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AvailableRevisions, Revision } from 'redux-store/models'

import {
  setDocuments,
  setMainDocument,
} from '../../Documents/api/currentDocumentsSlice'
import {
  practicesApi,
  setCurrentRevisionLoading,
  setCurrentRevision,
  selectCurrentRevision,
  setAvailableRevisions,
} from '../api'

/**
 *
 * Hook used for setting the revision status in query params according to view type(front-office/back-office) and practice ID
 *
 * @param isFrontOffice - {Boolean} - is the current view front office?
 * @param practiceId - {string} - id of the practice for which the status needs to be set
 * @returns current revision
 */
export const useParamRevisionStatus = (
  isFrontOffice: boolean,
  practiceId: string
) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const currentRevision = useAppSelector(selectCurrentRevision)
  const httpGetPractice = practicesApi.useGetPracticeByIdQuery(practiceId, {
    skip: !practiceId,
  })
  const [httpGetRevision] = practicesApi.useLazyGetRevisionByStatusQuery()

  const setRevisionParam = useCallback(
    (param: 'published' | 'draft' | 'approval') => {
      const newParams = new URLSearchParams(searchParams)
      if (param) {
        newParams.set(PracticeQueryParams.REVISION_STATUS, param)
      } else {
        newParams.delete(PracticeQueryParams.REVISION_STATUS)
      }
      navigate(
        { search: newParams.toString(), hash: location.hash },
        { replace: true }
      )
    },
    [searchParams, navigate, location.hash]
  )

  const fetchRevision = useCallback(
    async (param: 'published' | 'draft' | 'approval') => {
      dispatch(setCurrentRevisionLoading(true))
      try {
        const resp = await httpGetRevision({
          id: practiceId,
          status: param,
        }).unwrap()
        if (!resp || !resp.hasOwnProperty('data')) {
          throw Error('no revision data')
        }
        dispatch(setCurrentRevision(resp.data))
        dispatch(setMainDocument(resp.data.main_document[0]))
        dispatch(setDocuments(resp.data.documents))
        dispatch(setCurrentRevisionLoading(false))
      } catch (err: any) {
        dispatch(setCurrentRevisionLoading(false))
        if (param === 'approval') {
          dispatch(
            setAvailableRevisions({
              approval: false,
            } as AvailableRevisions)
          )
        }
        setRevisionParam('draft')
      }
    },
    [dispatch, httpGetRevision, practiceId, setRevisionParam]
  )

  useEffect(() => {
    if (!httpGetPractice.isSuccess) return

    const paramRevision = searchParams.get(PracticeQueryParams.REVISION_STATUS)

    // set revision to draft in Back-office if no parameter is set
    if (!paramRevision) {
      setRevisionParam('draft')
      return
    }

    if (currentRevision && paramRevision !== currentRevision?.status) {
      // exception for preapproval as params do not handle it
      if (
        paramRevision === 'approval' &&
        currentRevision?.status === 'preapproval'
      ) {
        return
      }

      fetchRevision(paramRevision as any)
    }
  }, [
    dispatch,
    httpGetPractice.isSuccess,
    searchParams,
    setRevisionParam,
    currentRevision,
    fetchRevision,
  ])

  // set initial revision & available revisions based on guideline
  useEffect(() => {
    let revision: null | Revision = null
    dispatch(setCurrentRevisionLoading(true))

    if (httpGetPractice.isSuccess) {
      revision = httpGetPractice.data.data.current_draftish_revision
      if (httpGetPractice.data?.data?.available_revisions) {
        dispatch(
          setAvailableRevisions(httpGetPractice.data.data.available_revisions)
        )
      }
    }

    dispatch(setCurrentRevision(revision))
    dispatch(setCurrentRevisionLoading(false))

    return () => {
      dispatch(setCurrentRevision(null))
      dispatch(setCurrentRevisionLoading(false))
    }
  }, [dispatch, httpGetPractice.data, httpGetPractice.isSuccess])
}
